import { useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useCallback } from 'react';
import styled from 'styled-components';

import { Icon, Icons } from '@ge/components/icon';
import { Severity } from '@ge/components/severity';
import { EntityType } from '@ge/models/constants';
import { getFriendlyTimestamp } from '@ge/shared/util';

import { FormSection } from '../../task-template-shared';

const ParentEntityIcon = styled(Icon).attrs(({ icon, theme }) => ({
  color: theme?.newTaskDialog?.headerSubtitleColor,
  icon,
  size: 12,
}))``;

// TODO: pull entity details shared components out of app so can be shared here
export const EntityIcon = styled(Icon).attrs(({ theme }) => ({
  size: 20,
  color: theme?.newTaskDialog?.headerIconColor,
}))``;

const Container = styled.div`
  margin-bottom: 12px;

  .components {
    text-transform: capitalize;
  }

  .entity-title {
    align-items: start;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;

    .icons {
      align-items: center;
      display: flex;
      flex-flow: row nowrap;

      > * {
        + * {
          margin-left: 4px;
        }
      }
    }

    .timestamp {
      color: ${({ theme }) => theme?.newTaskDialog?.headerSubtitleColor};
      margin-left: auto;
      text-align: right;
    }

    .title {
      .name {
        color: ${({ theme }) => theme?.newTaskDialog?.headerTitleColor};
        font-size: 14px;
        font-weight: 500;
        padding: 3px 0 8px;
      }
    }

    > * {
      + * {
        margin-left: 4px;
      }
    }
  }

  .parent-details {
    .name {
      margin-left: 4px;
    }

    .entity-name {
      &:first-of-type {
        margin-right: 4px;
      }

      &:last-of-type {
        margin-left: 4px;
      }
    }
  }

  .subtitle {
    color: ${({ theme }) => theme?.newTaskDialog?.headerSubtitleColor};
    font-size: 12px;
    font-weight: 300;
  }

  > * {
    + * {
      margin-top: 8px;
    }
  }
`;

const ParentEntityContainer = ({ name, type }) => {
  // TODO: add support for more types in here as needed
  const icon = {
    [EntityType.SITE]: Icons.SITE,
    [EntityType.TURBINE]: Icons.TURBINE,
  }[type];

  return (
    <span className="entity-name">
      <ParentEntityIcon icon={icon} />
      <span className="name subtitle">{name}</span>
    </span>
  );
};

ParentEntityContainer.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(EntityType)).isRequired,
};

export const EntityHeaderSection = ({ entity, entityType }) => {
  const { getComponentTypeDescription } = useStoreState((state) => state.tenant);

  /*Get component description match from getComponentTypeDescription useStoreState */
  const findComponentTypes0 = useCallback(
    (componentDesc0) => {
      let matchingDes = '';
      const entityComp0 = componentDesc0?.split('~')[0];
      const entitySplit1 = componentDesc0?.split('~')[1];
      getComponentTypeDescription?.forEach((c) => {
        if (c?.componentTypeId !== undefined && entityComp0 !== undefined) {
          if (c?.componentTypeId === entityComp0) {
            matchingDes = c?.description;
          }
        }
      });
      return `${matchingDes} ${entitySplit1}`;
    },
    [getComponentTypeDescription],
  );

  const findComponentTypes1 = useCallback(
    (componentDesc1) => {
      let matchingDes = '';
      const entityComp1 = componentDesc1?.split('~')[0];
      const entitySplit1 = componentDesc1?.split('~')[1];
      getComponentTypeDescription?.forEach((c) => {
        if (c?.componentTypeId !== undefined && entityComp1 !== undefined) {
          if (c?.componentTypeId === entityComp1) {
            matchingDes = c?.description;
          }
        }
      });
      return `${matchingDes} ${entitySplit1}`;
    },
    [getComponentTypeDescription],
  );

  const findComponentTypes2 = useCallback(
    (componentDesc2) => {
      let matchingDes = '';
      const entityComp2 = componentDesc2?.split('~')[0];
      const entitySplit1 = componentDesc2?.split('~')[1];
      getComponentTypeDescription?.forEach((c) => {
        if (c?.componentTypeId !== undefined && entityComp2 !== undefined) {
          if (c?.componentTypeId === entityComp2) {
            matchingDes = c?.description;
          }
        }
      });
      return `${matchingDes} ${entitySplit1}`;
    },
    [getComponentTypeDescription],
  );

  const findComponentTypesDesc = useCallback(
    (component) => {
      let arrayFilter = [];
      if (component !== undefined && component?.length) {
        const componentType = component?.toString()?.split(' / ');
        const componentDesc0 = componentType && componentType[0] ? componentType[0] : '';
        const componentDesc1 = componentType && componentType[1] ? componentType[1] : '';
        const componentDesc2 = componentType && componentType[2] ? componentType[2] : '';
        const componentTypes0 = findComponentTypes0(componentDesc0);
        const componentTypes1 = findComponentTypes1(componentDesc1);
        const componentTypes2 = findComponentTypes2(componentDesc2);
        !!componentTypes0 && arrayFilter.push(componentTypes0);
        !!componentDesc1 && arrayFilter.push(componentTypes1);
        !!componentDesc2 && arrayFilter.push(componentTypes2);
        return arrayFilter.join(' / ');
      }
    },
    [findComponentTypes0, findComponentTypes1, findComponentTypes2],
  );
  let detail;
  // if we need to allow providing a specific time can get in props and pass in here
  const timestamp = getFriendlyTimestamp();
  const [time, date] = timestamp.split(', ');

  // TODO: add support for readonly views for other entity types as needed
  switch (entityType) {
    case EntityType.CASE: {
      const { asset = {}, componentIds = [], site = {} } = entity;

      detail = (
        <>
          <div className="parent-details">
            <ParentEntityContainer name={site.name} type={EntityType.SITE} />{' '}
            <span className="subtitle">/</span>{' '}
            <ParentEntityContainer name={asset.name} type={EntityType.TURBINE} />
          </div>
          <div className="entity-title">
            <div className="icons">
              <EntityIcon icon={Icons.ANOMALY} />
              <Severity level={entity.priority} size={12} viewBox="0 0 20 15" />
            </div>
            <div className="title">
              <div className="name">{entity.description}</div>
              <div className="components subtitle">
                {findComponentTypesDesc(componentIds?.join(' / '))}
              </div>
            </div>
            <div className="timestamp">
              <div>{time}</div>
              <div>{date}</div>
            </div>
          </div>
        </>
      );

      break;
    }

    default:
      detail = null;
  }

  if (!detail) {
    return null;
  }

  return (
    <FormSection>
      <Container>{detail}</Container>
    </FormSection>
  );
};

EntityHeaderSection.propTypes = {
  entity: PropTypes.object.isRequired,
  entityType: PropTypes.oneOf(Object.values(EntityType)).isRequired,
};
